import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import AuthPage from './pages/AuthPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import IdeaGenerator from './pages/IdeaGenerator';
import WritingTools from './pages/WritingTools';
import ScriptBuilder from './pages/ScriptBuilder';
import Editor from './pages/Editor';
import Voiceovers from './pages/Voiceovers';
import Planner from './pages/Planner';
import SavedContent from './pages/SavedContent';
import CreditActivity from './pages/CreditActivity';
import Billing from './pages/Billing';
import HelpPage from './pages/HelpPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import VerifyEmail from './pages/VerifyEmail';
import ContentAnalyzer from './pages/ContentAnalyzer';
import LandingPage from './pages/LandingPage';

// Layout
import Layout from './components/Layout';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(222,47%,11%)] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(222,47%,11%)] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      
      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/ideas" element={<IdeaGenerator />} />
        <Route path="/writing" element={<WritingTools />} />
        <Route path="/scripts" element={<ScriptBuilder />} />
        <Route path="/editor" element={<Editor />} />
        <Route path="/voiceovers" element={<Voiceovers />} />
        <Route path="/analyzer" element={<ContentAnalyzer />} />
        <Route path="/planner" element={<Planner />} />
        <Route path="/saved" element={<SavedContent />} />
        <Route path="/activity" element={<CreditActivity />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/help" element={<HelpPage />} />
      </Route>
      
      {/* Email verification */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      
      {/* Public legal pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Landing page at root */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Catch all - redirect unknown routes to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          theme="dark"
          toastOptions={{
            style: {
              background: 'hsl(217 33% 17%)',
              border: '1px solid hsl(217 33% 22%)',
              color: 'white'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
